import apiClient from './client';

export type HomeBannerLinkType = 'external' | 'internal';

/** Android is consumption-only (ADR 0014) — never show Premium promos there. */
export type HomeBannerPlatform = 'ios' | 'android';

export interface HomeBanner {
  id: string;
  slug: string;
  title_uz: string;
  title_ru: string | null;
  title_kiril: string | null;
  subtitle_uz: string | null;
  subtitle_ru: string | null;
  subtitle_kiril: string | null;
  image_url: string | null;
  bg_color: string;
  bg_color_to: string | null;
  text_color: string;
  link_url: string | null;
  link_type: HomeBannerLinkType;
  cta_label_uz: string | null;
  cta_label_ru: string | null;
  cta_label_kiril: string | null;
  platforms: HomeBannerPlatform[];
  is_active: boolean;
  sort_order: number;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateHomeBannerPayload {
  slug: string;
  title_uz: string;
  title_ru?: string;
  title_kiril?: string;
  subtitle_uz?: string;
  subtitle_ru?: string;
  subtitle_kiril?: string;
  image_url?: string;
  bg_color?: string;
  bg_color_to?: string;
  text_color?: string;
  link_url?: string;
  link_type?: HomeBannerLinkType;
  cta_label_uz?: string;
  cta_label_ru?: string;
  cta_label_kiril?: string;
  platforms?: HomeBannerPlatform[];
  is_active?: boolean;
  sort_order?: number;
  starts_at?: string;
  ends_at?: string;
}

export interface UpdateHomeBannerPayload {
  title_uz?: string;
  title_ru?: string;
  title_kiril?: string;
  subtitle_uz?: string;
  subtitle_ru?: string;
  subtitle_kiril?: string;
  image_url?: string;
  bg_color?: string;
  bg_color_to?: string;
  text_color?: string;
  link_url?: string;
  link_type?: HomeBannerLinkType;
  cta_label_uz?: string;
  cta_label_ru?: string;
  cta_label_kiril?: string;
  platforms?: HomeBannerPlatform[];
  is_active?: boolean;
  sort_order?: number;
  starts_at?: string;
  ends_at?: string;
}

export interface BannerStatsParams {
  from?: string;
  to?: string;
  groupBy?: 'day' | 'week' | 'month';
}

export interface BannerStats {
  total_clicks: number;
  unique_users: number;
  unique_visitors: number;
  by_language: Array<{ language: string | null; clicks: number }>;
  timeseries: Array<{ date: string; clicks: number }>;
}

export const homeBannersApi = {
  list: async (): Promise<HomeBanner[]> => {
    const res = await apiClient.get<{ banners: HomeBanner[] }>('/admin/home-banners');
    return res.data.banners;
  },

  getById: async (id: string): Promise<HomeBanner> => {
    const res = await apiClient.get<HomeBanner>(`/admin/home-banners/${id}`);
    return res.data;
  },

  create: async (payload: CreateHomeBannerPayload): Promise<HomeBanner> => {
    const res = await apiClient.post<HomeBanner>('/admin/home-banners', payload);
    return res.data;
  },

  update: async (
    id: string,
    payload: UpdateHomeBannerPayload,
  ): Promise<HomeBanner> => {
    const res = await apiClient.patch<HomeBanner>(`/admin/home-banners/${id}`, payload);
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/home-banners/${id}`);
  },

  getStats: async (
    id: string,
    params: BannerStatsParams = {},
  ): Promise<BannerStats> => {
    const res = await apiClient.get<BannerStats>(
      `/admin/home-banners/${id}/stats`,
      { params },
    );
    return res.data;
  },
};
